// Pure, immutable plan-tree edit operations shared by the Session-tab coach
// (useWorkoutCoach) and the programme session editor (EditProgrammeSessionDialog,
// #59). Each takes a plan and returns a NEW plan (or the same reference when the
// edit is a no-op), so callers can drop them straight into a state updater:
//   setPlan((prev) => updateNode(prev, path, updates))
//
// `indexPath` is an array of child indices from the root (e.g. [0, 2] = root's
// child 0, then its child 2). An empty path targets the root node itself.

import { get, cloneDeep } from "lodash";
import { resolveSetScheme } from "./planVisuals";
import { isRecoveryType, isTimedUnit } from "./planNodeUtils";

const childPath = (indexPath) =>
  indexPath.map((i) => `children[${i}]`).join(".");

// A count unit makes a Group a repeat container, so its `value` is the number of
// sets its children run. A time unit makes it a fixed-time cap whose children
// are a checklist, never sets — see docs/schema.md.
const COUNT_UNITS = new Set(["rounds", "sets", "workout"]);

const isRepeatGroup = (node) =>
  node?.type === "Group" &&
  COUNT_UNITS.has(String(node.unit || "").toLowerCase());

export const resolveByIndexPath = (plan, indexPath) => {
  if (!Array.isArray(indexPath) || indexPath.length === 0) return plan;
  return get(plan, childPath(indexPath));
};

// How many sets the node at `indexPath` runs: its parent's repeat count when
// the parent is a count-unit Group, else 1. A node carries no notion of how
// often it repeats — that lives on the Group above it — so anything sizing a
// per-set control has to look up for it (issue #69).
export const enclosingSetCount = (plan, indexPath) => {
  if (!plan || !Array.isArray(indexPath) || indexPath.length === 0) return 1;
  const parent = resolveByIndexPath(plan, indexPath.slice(0, -1));
  if (!isRepeatGroup(parent)) return 1;
  const count = Number(parent.value);
  return Number.isFinite(count) && count > 0 ? Math.floor(count) : 1;
};

// Stable id generator: prefer crypto.randomUUID(), fall back to a
// counter-suffixed timestamp for older test runners.
let _dupIdCounter = 0;
export const genStableId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  _dupIdCounter += 1;
  return `dup-${Date.now().toString(36)}-${_dupIdCounter}`;
};

// Deep-clone a subtree, regenerating every id (top-level + every descendant).
// Required for React keys / dnd-kit SortableContext stability after a duplicate.
export const deeplyRegenerateIds = (node) => {
  if (!node || typeof node !== "object") return node;
  const next = { ...node, id: genStableId() };
  if (Array.isArray(next.children)) {
    next.children = next.children.map(deeplyRegenerateIds);
  }
  return next;
};

// Every `setScheme` array holds exactly one entry per set (docs/schema.md), so
// changing a Group's set count leaves its children's schemes the wrong length —
// which validatePlan.js reports as blocking. Re-fit rather than reject: added
// sets repeat the last prescribed figure, removed sets drop off the end. Set
// COUNT still comes from the Group alone, so this only keeps the plan tidy; it
// can never change how many sets run.
const refitSetSchemes = (group, setCount) => {
  if (!Array.isArray(group?.children)) return;
  if (!Number.isFinite(setCount) || setCount < 1) return;
  for (const child of group.children) {
    const scheme = child?.setScheme;
    if (!scheme || typeof scheme !== "object") continue;
    for (const dim of Object.keys(scheme)) {
      const arr = scheme[dim];
      if (!Array.isArray(arr) || arr.length === 0) continue;
      if (arr.length === setCount) continue;
      scheme[dim] =
        arr.length > setCount
          ? arr.slice(0, setCount)
          : arr.concat(Array(setCount - arr.length).fill(arr[arr.length - 1]));
    }
  }
};

// Merge `updates` onto the node at indexPath. Empty path edits the root (used to
// rename the session title/description).
export const updateNode = (prev, indexPath, updates) => {
  if (!prev) return prev;
  const plan = cloneDeep(prev);
  const isRoot = !Array.isArray(indexPath) || indexPath.length === 0;
  const node = isRoot ? plan : get(plan, childPath(indexPath));
  if (!node) return prev;
  const previousValue = node.value;
  Object.assign(node, updates);
  if (isRepeatGroup(node) && node.value !== previousValue) {
    refitSetSchemes(node, Number(node.value));
  }
  return plan;
};

export const deleteNode = (prev, indexPath) => {
  if (!prev || !Array.isArray(indexPath) || indexPath.length === 0) return prev;
  const plan = cloneDeep(prev);
  const parentPath = indexPath.slice(0, -1);
  const childIndex = indexPath[indexPath.length - 1];
  const parent =
    parentPath.length === 0 ? plan : get(plan, childPath(parentPath));
  if (!parent?.children) return prev;
  parent.children.splice(childIndex, 1);
  return plan;
};

export const addNode = (prev, parentIndexPath, newNode, insertIndex) => {
  if (!prev) return prev;
  const plan = cloneDeep(prev);
  const parent =
    parentIndexPath.length === 0 ? plan : get(plan, childPath(parentIndexPath));
  if (!parent) return prev;
  if (!parent.children) parent.children = [];
  const idx = insertIndex ?? parent.children.length;
  parent.children.splice(idx, 0, newNode);
  return plan;
};

// Move a node within the tree. Handles same-parent reorder and cross-parent
// moves. Same-parent reorder adjusts `toIndex` when the source sits before the
// target (the splice-out shifts everything above it down by one) — equivalent
// to dnd-kit's arrayMove. Rejects moves that would nest a node inside itself.
export const moveNode = (prev, fromIndexPath, toParentIndexPath, toIndex) => {
  if (!Array.isArray(fromIndexPath) || fromIndexPath.length === 0) return prev;
  if (!Array.isArray(toParentIndexPath)) return prev;
  // Self-nest guard: target parent path can't start with the source path.
  if (toParentIndexPath.length >= fromIndexPath.length) {
    const isDescendant = fromIndexPath.every(
      (seg, i) => toParentIndexPath[i] === seg,
    );
    if (isDescendant) return prev;
  }
  if (!prev) return prev;
  const plan = cloneDeep(prev);
  const fromParentPath = fromIndexPath.slice(0, -1);
  const fromIndex = fromIndexPath[fromIndexPath.length - 1];
  const fromParent = resolveByIndexPath(plan, fromParentPath);
  if (!fromParent?.children?.[fromIndex]) return prev;
  const toParent = resolveByIndexPath(plan, toParentIndexPath);
  if (!toParent) return prev;
  if (!toParent.children) toParent.children = [];
  const [moved] = fromParent.children.splice(fromIndex, 1);
  const samePath =
    fromParentPath.length === toParentIndexPath.length &&
    fromParentPath.every((s, i) => s === toParentIndexPath[i]);
  const insertAt = samePath && toIndex > fromIndex ? toIndex - 1 : toIndex;
  const clamped = Math.max(0, Math.min(insertAt, toParent.children.length));
  toParent.children.splice(clamped, 0, moved);
  return plan;
};

export const duplicateNode = (prev, indexPath) => {
  if (!Array.isArray(indexPath) || indexPath.length === 0) return prev;
  if (!prev) return prev;
  const plan = cloneDeep(prev);
  const parentPath = indexPath.slice(0, -1);
  const childIndex = indexPath[indexPath.length - 1];
  const parent =
    parentPath.length === 0 ? plan : get(plan, childPath(parentPath));
  if (!parent?.children?.[childIndex]) return prev;
  const clone = deeplyRegenerateIds(parent.children[childIndex]);
  parent.children.splice(childIndex + 1, 0, clone);
  return plan;
};

// The parent every path in `indexPaths` shares, or null if they aren't
// siblings (or any of them is the root). Wrapping is only defined for
// siblings: nodes from two different parents have no single position the new
// block could occupy.
export const sharedParentPath = (indexPaths) => {
  if (!Array.isArray(indexPaths) || indexPaths.length === 0) return null;
  let parentPath = null;
  for (const path of indexPaths) {
    if (!Array.isArray(path) || path.length === 0) return null;
    const candidate = path.slice(0, -1);
    if (parentPath === null) {
      parentPath = candidate;
      continue;
    }
    const known = parentPath;
    if (
      candidate.length !== known.length ||
      !candidate.every((seg, i) => known[i] === seg)
    ) {
      return null;
    }
  }
  return parentPath;
};

// Wrap sibling nodes in a new Group, so an existing flat session can be
// organised into blocks ("Legs" holding squats and deadlifts) without
// rebuilding it a node at a time.
//
// The block lands at the position of the TOPMOST selected node and holds the
// selection in document order. It takes exactly what was selected, never the
// span between the first and last — an unselected sibling can't be swallowed
// by a wrap it wasn't part of. A rest sitting between two wrapped exercises
// and left unselected therefore stays outside the block, which is what its
// row on screen implies.
//
// `groupNode` is the container to create (label/unit/value come from the
// caller); its `children` are supplied here and any it arrives with are
// ignored. Returns the same reference for a no-op or an invalid selection, so
// a stale selection can never scramble the tree.
export const wrapNodesInGroup = (prev, indexPaths, groupNode) => {
  if (!prev || !groupNode) return prev;
  const parentPath = sharedParentPath(indexPaths);
  if (!parentPath) return prev;
  const indices = [
    ...new Set(indexPaths.map((path) => path[path.length - 1])),
  ].sort((a, b) => a - b);
  if (indices.length === 0) return prev;

  const plan = cloneDeep(prev);
  const parent =
    parentPath.length === 0 ? plan : get(plan, childPath(parentPath));
  if (!Array.isArray(parent?.children)) return prev;
  if (indices.some((i) => !parent.children[i])) return prev;

  const wrapped = indices.map((i) => parent.children[i]);
  const insertAt = indices[0];
  // Splice out high-to-low so the lower indices stay valid as we go.
  for (let k = indices.length - 1; k >= 0; k -= 1) {
    parent.children.splice(indices[k], 1);
  }
  parent.children.splice(insertAt, 0, {
    type: "Group",
    ...groupNode,
    id: groupNode.id || genStableId(),
    children: wrapped,
  });
  return plan;
};

// A block that repeats a whole number of times: a Group with a repeat unit, or
// with no unit at all (the runtime reads that as a count; translatePlan).
// Time-capped blocks (AMRAP) run once for a cap, so there is nothing to split.
export const isSplittableGroup = (node) => {
  if (node?.type !== "Group" || isTimedUnit(node.unit)) return false;
  if (node.unit && !COUNT_UNITS.has(String(node.unit).toLowerCase())) return false;
  const n = Number(node.value);
  return Number.isInteger(n) && n >= 2 && Array.isArray(node.children);
};

// The rest the compiler strips after a block's final round
// (applyRestFilter): a short, plain recovery as the block's last child.
const ROUND_REST_STRIP_SECONDS = 60;
const isStrippedRoundRest = (node) => {
  if (!node || !isRecoveryType(node.type) || node.type === "Transition") {
    return false;
  }
  const sec = Number(node.value) * (node.unit === "m" ? 60 : 1);
  return Number.isFinite(sec) && sec < ROUND_REST_STRIP_SECONDS;
};

const workLeaves = (node, out = []) => {
  if (Array.isArray(node?.children)) {
    node.children.forEach((child) => workLeaves(child, out));
  } else if (node && !isRecoveryType(node.type)) {
    out.push(node);
  }
  return out;
};

// How many times each node runs inside ONE round of `node`: the product of the
// repeat counts of the Groups between them. A recorded log lists a node once
// per run, so this is what slices it back into rounds.
const runsPerRound = (node, factor = 1, out = {}) => {
  for (const child of node.children || []) {
    if (child.id) out[child.id] = factor;
    if (Array.isArray(child.children)) {
      const n = Number(child.value);
      const reps =
        child.type === "Group" && !isTimedUnit(child.unit) && n >= 1 ? n : 1;
      runsPerRound(child, factor * reps, out);
    }
  }
  return out;
};

// Ungroup: replace a block that runs once with its children, in place — the
// reverse of wrapNodesInGroup. A block that repeats (or is time-capped)
// shapes what runs, so removing it would change the session; those are
// refused. When the block holds a single child block, that child takes the
// block's name: a split round named "Squats" around a "Tabata" was named for
// what the Tabata held. An exercise keeps its own name.
export const isUngroupableNode = (node) => {
  if (!node || !Array.isArray(node.children)) return false;
  if (node.type === "Circuit") return true;
  if (node.type !== "Group" || isTimedUnit(node.unit)) return false;
  const n = Number(node.value ?? 1);
  return Number.isFinite(n) && n <= 1;
};

const PLACEHOLDER_BLOCK_NAMES = new Set(["", "new block", "block"]);

export const ungroupNode = (prev, indexPath) => {
  if (!prev || !Array.isArray(indexPath) || indexPath.length === 0) return prev;
  const block = resolveByIndexPath(prev, indexPath);
  if (!isUngroupableNode(block)) return prev;
  const plan = cloneDeep(prev);
  const parentPath = indexPath.slice(0, -1);
  const parent =
    parentPath.length === 0 ? plan : get(plan, childPath(parentPath));
  const at = indexPath[indexPath.length - 1];
  const children = cloneDeep(block.children);
  const working = children.filter((c) => !isRecoveryType(c.type));
  const name = String(block.label || "").trim();
  if (
    working.length === 1 &&
    Array.isArray(working[0].children) &&
    !PLACEHOLDER_BLOCK_NAMES.has(name.toLowerCase())
  ) {
    working[0].label = name;
  }
  parent.children.splice(at, 1, ...children);
  return plan;
};

// Copy a round's subtree. Round 0 keeps the original ids so the rows on screen
// don't remount; later rounds get fresh ones. `idMap` records old → new.
const copyRound = (node, round, idMap) => {
  const id = round === 0 || !node.id ? node.id : genStableId();
  if (node.id) idMap[node.id] = id;
  const next = { ...node, id };
  if (Array.isArray(node.children)) {
    next.children = node.children.map((child) => copyRound(child, round, idMap));
  }
  return next;
};

// Unroll a repeating block into its rounds, in place: "Set × 3" of a Tabata
// and a set rest becomes Tabata, rest, Tabata, rest, Tabata, each nameable
// for what was done in it. A round of several exercises stays wrapped in a
// one-round block instead. Splitting changes nothing about what runs:
//   • each child's per-set scheme is resolved to that round's figures;
//   • the final round's trailing short rest, which the compiler strips after a
//     block's last round, is dropped from the last copy;
//   • on a saved session, the recorded log is moved onto the copies, so round
//     2's times show under round 2.
// `names[k]` names round k: its one child when unwrapped, else its block; and
// the exercise inside too when there is exactly one. A blank name keeps the original. Returns `prev` when the node
// isn't a splittable block.
export const splitGroupIntoRounds = (prev, indexPath, names = []) => {
  if (!prev || !Array.isArray(indexPath) || indexPath.length === 0) return prev;
  const original = resolveByIndexPath(prev, indexPath);
  if (!isSplittableGroup(original)) return prev;

  const rounds = Number(original.value);
  const idMaps = [];
  const renamedLeaf = {};
  const copies = [];
  for (let k = 0; k < rounds; k += 1) {
    const idMap = {};
    const copy = copyRound(cloneDeep(original), k, idMap);
    copy.value = 1;
    copy.unit = original.unit || "rounds";
    copy.children = copy.children.map((child) => {
      if (!child.setScheme) return child;
      const { setScheme, ...rest } = resolveSetScheme(child, k);
      return rest;
    });
    if (k === rounds - 1 && isStrippedRoundRest(copy.children[copy.children.length - 1])) {
      copy.children = copy.children.slice(0, -1);
    }
    // A round holding one block or exercise (plus rests) needs no wrapper: a
    // block that runs once groups nothing, so its contents go in place and
    // the name lands on that one child. A round of several exercises keeps
    // its block, which is what holds them together and carries the name.
    const working = copy.children.filter((c) => !isRecoveryType(c.type));
    const unwrap = working.length === 1;
    const named = unwrap ? working[0] : copy;
    const name = String(names[k] ?? "").trim();
    if (name) {
      named.label = name;
      const leaves = workLeaves(named);
      if (leaves.length === 1) {
        leaves[0].label = name;
        renamedLeaf[leaves[0].id] = name;
      }
    }
    idMaps.push(idMap);
    copies.push(...(unwrap ? copy.children : [copy]));
  }

  const plan = cloneDeep(prev);
  const parentPath = indexPath.slice(0, -1);
  const parent =
    parentPath.length === 0 ? plan : get(plan, childPath(parentPath));
  const at = indexPath[indexPath.length - 1];
  parent.children.splice(at, 1, ...copies);

  // A client-recorded log names each run's node; the j-th run of a node falls
  // in round floor(j / runs-per-round). Logs without node ids (the server's
  // fallback record) carry nothing to move and are left alone.
  if (Array.isArray(plan.phase_log)) {
    const perRound = runsPerRound(original);
    const seen = {};
    plan.phase_log = plan.phase_log.map((entry) => {
      const oldId = entry?.nodeId;
      if (!oldId || !(oldId in perRound)) return entry;
      const j = seen[oldId] || 0;
      seen[oldId] = j + 1;
      const round = Math.min(rounds - 1, Math.floor(j / perRound[oldId]));
      const nodeId = idMaps[round][oldId] || oldId;
      return {
        ...entry,
        nodeId,
        ...(renamedLeaf[nodeId] ? { nodeLabel: renamedLeaf[nodeId] } : {}),
      };
    });
  }
  return plan;
};
